import React from 'react';
import { Link } from 'react-router-dom';
import { EventDetails } from '../types';
import { eventImageUrl } from '../seo/eventMeta';
import { countdownLabel, formatEventDate } from '../lib/events';
import EventTypeTag from './eventTypeTag';

interface EventCardProps {
  event: EventDetails;
  // Omit both to hide the favourite star.
  favourited?: boolean;
  onToggleFavourite?: (event: EventDetails) => void;
}

const API_URL = process.env.REACT_APP_API_URL;

const EventCard: React.FC<EventCardProps> = ({ event, favourited, onToggleFavourite }) => {
  const place = [event.City, event.County].filter(Boolean).join(', ');
  const away = event.DistanceKm != null ? `${event.DistanceKm} km away` : '';
  const placeLine = [place, away].filter(Boolean).join(' · ');

  return (
    <div className="group relative flex flex-col overflow-hidden rounded-2xl border border-surface-subtle bg-white shadow-card transition-shadow hover:shadow-card-hover">
      <Link to={`/event/${event.Id}`} className="flex flex-1 flex-col">
        <div className="aspect-[16/9] w-full overflow-hidden bg-surface-muted">
          {API_URL && (
            <img
              src={eventImageUrl(API_URL, event.Id)}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2 p-4">
          <div className="flex items-center justify-between gap-2">
            <EventTypeTag eventType={event.EventType} />
            <span className="text-xs font-medium text-ink-faint">{countdownLabel(event.EventDate)}</span>
          </div>
          <h3 className="text-base font-semibold leading-snug text-ink group-hover:text-brand-600">{event.Name}</h3>
          <p className="text-sm text-ink-muted">{formatEventDate(event.EventDate)}</p>
          {placeLine && <p className="text-sm text-ink-muted">{placeLine}</p>}
          {event.Distance && <p className="mt-auto text-xs text-ink-faint">{event.Distance}</p>}
        </div>
      </Link>
      {onToggleFavourite && (
        <button
          type="button"
          onClick={() => onToggleFavourite(event)}
          aria-label={favourited ? `Remove ${event.Name} from your event planner` : `Add ${event.Name} to your event planner`}
          aria-pressed={Boolean(favourited)}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-card backdrop-blur hover:bg-white"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"
            fill={favourited ? '#f4631c' : 'none'} stroke={favourited ? '#f4631c' : '#5b6675'}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default EventCard;
